import { Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { Box, Chip, Dialog, DialogContent, DialogTitle, Divider, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { getInsIdentityResultMessage } from '@/constants/dmp-connect'
import { InsAuditLog, InsIdentityResultCode, InsOperation } from '@/core/models/ins-audit.model'
import { getGenderOptions, insSexToPatientGender } from '@/utils/patient'
import { getResultDisplay } from './InsAuditResultChip'

type InsiReturnLogDialogProps = {
  open: boolean
  onClose: () => void
  log: InsAuditLog
}

function AttributeRow({ label, value }: { label: React.ReactNode; value?: string }) {
  return (
    <Box display="flex" justifyContent="space-between" gap={2} py={0.5}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} textAlign="right">
        {value || '—'}
      </Typography>
    </Box>
  )
}

export function InsiReturnLogDialog({ open, onClose, log }: InsiReturnLogDialogProps) {
  const lingui = useLingui()
  const isReception = log.operation === InsOperation.RECEIVE_EXTERNAL_IDENTITY
  const identity = log.returnedIdentity ?? log.receivedIdentity
  const isVerification = log.operation === InsOperation.CHECK_INS_IDENTITY
  const resultCode = typeof log.resultCode === 'number' ? log.resultCode : InsIdentityResultCode.NO_IDENTITY_FOUND
  const { displayCode, color } = getResultDisplay(resultCode)
  const returnCodeLabel = `${displayCode} - ${lingui._(getInsIdentityResultMessage(resultCode))}`

  const sexLabel = (() => {
    if (!identity?.sex) return undefined
    const gender = insSexToPatientGender(Number(identity.sex))
    const option = getGenderOptions().find((o) => o.value === gender)
    return option ? `${lingui._(option.label)} (${identity.sex})` : identity.sex
  })()

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
          {isReception ? <Trans>Incoming external identity flux log</Trans> : <Trans>INSi teleservice return log</Trans>}
          <IconButton onClick={onClose} size="small" aria-label="close">
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {isReception ? (
          <Box display="flex" alignItems="center" gap={1.5} mb={2}>
            <Typography variant="body2" color="text.secondary">
              <Trans>Emitter</Trans>
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {log.emitter || '—'}
            </Typography>
          </Box>
        ) : (
          <>
            <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
              <Typography variant="body2" color="text.secondary">
                <Trans>Return code</Trans>
              </Typography>
              <Chip label={returnCodeLabel} color={color} size="small" />
            </Box>

            {log.returnStatus && (
              <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                <Typography variant="body2" color="text.secondary">
                  <Trans>Return status</Trans>
                </Typography>
                {log.returnStatus === 'OK' ? (
                  <Chip label={<Trans>Validated (OK)</Trans>} color="success" size="small" />
                ) : (
                  <Chip label={<Trans>Not validated (KO)</Trans>} color="error" size="small" />
                )}
              </Box>
            )}
          </>
        )}

        <Typography variant="subtitle2" gutterBottom>
          {isReception ? (
            <Trans>Attributes received from the emitter</Trans>
          ) : isVerification ? (
            <Trans>Verified attributes</Trans>
          ) : (
            <Trans>Returned attributes</Trans>
          )}
        </Typography>
        <AttributeRow label={<Trans>INS identity number</Trans>} value={identity?.insNIR} />
        <AttributeRow label={<Trans>OID</Trans>} value={identity?.insOID} />
        <AttributeRow label={<Trans>Birth name</Trans>} value={identity?.birthName} />
        <AttributeRow label={<Trans>Birth given name(s)</Trans>} value={identity?.birthGiven} />
        <AttributeRow label={<Trans>Birth date</Trans>} value={identity?.birthDate} />
        <AttributeRow label={<Trans>Sex</Trans>} value={sexLabel} />
        <AttributeRow label={<Trans>Birth place code</Trans>} value={identity?.birthPlaceCode} />

        {identity?.insHistory && identity.insHistory.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              <Trans>INS matricule history</Trans>
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Trans>INS identity number</Trans>
                  </TableCell>
                  <TableCell>
                    <Trans>OID</Trans>
                  </TableCell>
                  <TableCell>
                    <Trans id="ins.history.startDate">Start date</Trans>
                  </TableCell>
                  <TableCell>
                    <Trans id="ins.history.endDate">End date</Trans>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {identity.insHistory.map((history, index) => (
                  <TableRow key={index}>
                    <TableCell>{`${history.value ?? ''}${history.key ?? ''}` || '—'}</TableCell>
                    <TableCell>{history.oid || '—'}</TableCell>
                    <TableCell>{history.startDate || '—'}</TableCell>
                    <TableCell>{history.endDate || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
